import React, { useCallback } from 'react';
import { pickAriaAndDataProps } from '@tanium/coreui-utils';
import { ButtonBase, ButtonBaseProps } from '@tanium/react-button-base';
import styled from '@tanium/react-emotion-9';
import { css } from '@tanium/style-system';
import { ThemeFontWeights } from '@tanium/theme-data';
import {
  ButtonBorder,
  ButtonFill,
  ButtonHoverFill,
  ButtonHoverText,
  ButtonSelectedBorder,
  ButtonSelectedFill,
  ButtonSelectedText,
  ButtonText,
} from './themeDefinitions';

interface ToggleEvent {
  /**
   * The new selected state.
   */
  selected: boolean;
}

export type ToggleEventHandler = (e: ToggleEvent) => void;

export interface ToggleButtonProps {
  /**
   * A callback to be invoked when the button is toggled.
   */
  onToggle?: ToggleEventHandler;
  /**
   * Whether the button should be selected or not.
   */
  selected?: boolean;
  /**
   * Size of the button.
   */
  size?: ButtonBaseProps['size'];
  /**
   * The button's content.
   */
  children?: React.ReactNode;
}

const StyledButtonBase = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean | undefined }>(({ selected }) =>
  css({
    bg: selected ? ButtonSelectedFill : ButtonFill,
    color: selected ? ButtonSelectedText : ButtonText,
    ['&:hover, &:active' as string]: {
      bg: ButtonHoverFill,
      color: ButtonHoverText,
    },
    border: 1,
    borderColor: selected ? ButtonSelectedBorder : ButtonBorder,
    fontWeight: ThemeFontWeights.Medium,
  }),
);

/**
 * A toggle button.
 */
export const ToggleButton = ({
  size,
  selected,
  onToggle,
  children,
  ...props
}: ToggleButtonProps) => {
  const handleClick = useCallback(() => {
    if (onToggle) {
      onToggle({ selected: !selected });
    }
  }, [onToggle, selected]);

  return (
    <StyledButtonBase
      aria-pressed={selected}
      {...pickAriaAndDataProps(props)}
      type="button"
      onClick={handleClick}
      size={size}
      border={1}
      selected={selected}
    >
      {children}
    </StyledButtonBase>
  );
};
